/*
 * Copyright (c) 2019. Sonrai Analytics Ltd.
 * Developed by Ryan Duffy & Warren Dowey
 */

import { Injectable } from '@angular/core';
import { HistoryActionModel } from '@app/models/history-action.model';
import { ApiBaseService } from '@app/services/api-services/api-base.service';
import { HttpClient } from '@angular/common/http';
import { ApiParamsModel } from '@app/models/api-params.model';
import { ApiFilterModel } from '@app/models/api-filter.model';
import { Subscription } from 'rxjs';
import { AuthUserService } from '@app/services/authentication/auth-user-service';
@Injectable({
  providedIn: 'root',
})
export class HistoryActionService extends ApiBaseService {
  private _historyAction: HistoryActionModel;
  protected _endpoint = 'history_actions';
  private eventActionSubscription: Subscription;

  constructor(http: HttpClient, authUserService: AuthUserService) {
    super(http, authUserService);
  }

  get historyAction(): HistoryActionModel {
    return this._historyAction;
  }

  set historyAction(value: HistoryActionModel) {
    this._historyAction = value;
  }

  /**
   *  Warren Dowey & Ryan Duffy
   *
   *  getHistoryActionById
   *
   * : Get an HistoryAction based on the id by filtering the API query
   *
   * @param id of type string
   *
   * @returns Void: gets the HistoryAction with the id that meets the query
   */
  public getHistoryActionById(historyActionId: string): void {
    const apiParamsModel = new ApiParamsModel();
    const apiFilterModel: ApiFilterModel = {
      field: 'history_action_id',
      operator: 'eq',
      value: historyActionId,
    };
    apiParamsModel.filter = [apiFilterModel];
    this.getData(apiParamsModel);
  }
  /**
   *  Warren Dowey & Ryan Duffy
   *
   *  getHistoryActionById
   *
   * : Get an HistoryAction based on the id by filtering the API query
   *
   * @param id of type string
   *
   * @returns Void: gets the HistoryAction with the id that meets the query
   */
  public getHistoryActionByEventId(eventId: string): void {
    const apiParamsModel = new ApiParamsModel();
    const apiFilterModel: ApiFilterModel = {
      field: 'event_id',
      operator: 'eq',
      value: eventId,
    };
    apiParamsModel.filter = [apiFilterModel];
    this.getData(apiParamsModel);
  }
  /**
   *  Ryan Duffy
   *
   *  getHistoryActionByIsProjectHistoryAction
   *
   * : Get an HistoryAction based on the id by filtering the API query
   *
   * @param id of type string
   *
   * @returns Void: gets the HistoryAction with the id that meets the query
   */
  public getHistoryActionByIsProjectHistoryAction(isProjectHistoryAction: boolean): void {
    const apiParamsModel = new ApiParamsModel();
    const apiFilterModel: ApiFilterModel = {
      field: 'is_project_history_action',
      operator: 'eq',
      value: isProjectHistoryAction,
    };
    apiParamsModel.filter = [apiFilterModel];
    this.getData(apiParamsModel);
  }

  /**
   * Warren Dowey & Ryan Duffy
   *
   *  handleData
   *
   * : handles the data from the list by extending the handleData function from the API base service
   *
   * @param data of type HistoryActionModel
   *
   * @returns Void: handle a get request from the postgres DB
   */
  protected handleData(data: HistoryActionModel[]): void {
    this.historyAction = data[0];
    super.handleData(data);
  }
  /**
   *  Warren Dowey & Ryan Duffy
   *
   *  handleInsert
   *
   * : handles the insert of a new HistoryAction into the list
   *
   * @param data of type HistoryActionModel
   *
   * @returns Void: inserts a new HistoryAction into the lsit
   */
  protected handleInsert(data: HistoryActionModel[]): void {
    if (data) {
      this.historyAction = data[0];
    }
    super.handleInsert(data);
  }
  /**
   *  Warren Dowey & Ryan Duffy
   *
   *  handleUpdate
   *
   * : handles the update of an HistoryAction in the list
   *
   * @param data of type HistoryActionModel
   *
   * @returns Void: update the lsit
   */
  protected handleUpdate(data: HistoryActionModel[]): void {
    this.historyAction = data[0];
    super.handleUpdate(data);
  }
  /**
   *  Warren Dowey & Ryan Duffy
   *
   *  handleDelete
   *
   * : deletes an HistoryAction from the list
   *
   * @param data of type HistoryActionModel
   *
   * @returns Void: deletes a HistoryAction from the list
   */
  protected handleDelete(data: HistoryActionModel[]): void {
    super.handleDelete(data);
  }

  ngOnDestroy(): void {
    if (this.eventActionSubscription) {
      this.eventActionSubscription.unsubscribe();
    }
  }
}
